import type { SaveAdChatParams } from '@module/apis/client-side/chat/type';
import SettingConfig from '@module/config/setting';
import parseEnterChat from '@module/helpers/chat/parseEnterChat';
import { useAdChatConfigQuery, useChatMessageMutation, useSaveAdChatMutation } from '@module/queries/chat/chatQueryHooks';
import type { NormalChatMessage } from '@module/types/centrifuge/public-message';
import type { IntendedAlertMessage } from '@module/types/centrifuge/public-message/etcMessageTypes';
import type { FrozenMessage } from '@module/types/centrifuge/public-message/freezeMessageTypes';
import type { CustomMuteOnMessage } from '@module/types/centrifuge/public-message/muteMessageTypes';
import checkIsAdChat from '@module/utils/checkIsAdChat';
import { BaseHttpResponseError } from '@module/utils/error/apiErrors';
import toHalfLetter from '@module/utils/toHalfLetter';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import useChatMessageStore from '../../stores/useChatMessageStore';
import useChatPanelStore from '../../stores/useChatPanelStore';
import { useFetchLivePlayMutationStore } from '../../stores/useFetchLivePlayMutationStore';
import useMuteStore from '../../stores/useMuteStore';
import useSocketStore from '../../stores/useSocketStore';
import type { FanLevel, NormalChatPayloadType } from '../../types/useCentrifugeTypes';
import { checkIsMobile } from '../../utils/checkIsMobile';
import useGetLoginInfoQuery from '../auth/useGetLoginInfoQuery';
import useChatInitiator from './useChatInitiator';
import useInduceBookmark from './useInduceBookmark';
interface Props {
  chatInputValue: string; // 내재되어 있으면, 열혈팬 탭에 갔다 왔을 시 적어둔 채팅 input value가 초기화되는 문제가 있음
  setChatInputValue: React.Dispatch<React.SetStateAction<string>>;
  onFreezeOnError?: (message: string) => void;
  onNeedLogin?: () => void;
  onNeedAuthDone?: () => void;
  messageOnPaperingOnError?: string;
  messageOnUnexpectedChatError?: string;
  messageOnSubmitChatOnPaperingBlockTime?: string;
  messageOnSubmitChatOnMuteTime?: string;
  messageOnSubmitChatOnFrozen?: string;
}
const useSendChatLogicAndCommonHooks = ({
  chatInputValue,
  setChatInputValue,
  onFreezeOnError,
  onNeedLogin,
  onNeedAuthDone,
  messageOnPaperingOnError = '지속적인 채팅으로인해 30초간 채팅을 금지합니다.',
  messageOnUnexpectedChatError = '채팅이 원활하지 않습니다. 잠시 후 다시 시도해주세요.',
  messageOnSubmitChatOnPaperingBlockTime = '도배로 인해 채팅금지 상태입니다.',
  messageOnSubmitChatOnMuteTime = '채팅금지 상태입니다.',
  messageOnSubmitChatOnFrozen = '채팅창이 잠겨 있습니다.'
}: Props) => {
  const [paperingBlockTime, setPaperingBlockTime] = useState(0);
  const [lastKeyPressedInChatInput, setLastKeyPressedInChatInput] = useState('default');
  const [typingStart, setTypingStart] = useState('');
  const roomEnterTime = useRef(dayjs().format('YYYY-MM-DD HH:mm:ss'));
  const chatInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const {
    userInfo,
    isLogin,
    isAuthDone
  } = useGetLoginInfoQuery();
  const {
    token,
    payloadInfo
  } = useSocketStore();
  const setChatMessages = useChatMessageStore(state => state.setChatMessages);
  const muteTime = useMuteStore(state_0 => state_0.muteTime);
  const setMuteTime = useMuteStore(state_1 => state_1.setMuteTime);
  const {
    data: playInfo
  } = useFetchLivePlayMutationStore();
  const {
    closeChatInputArea,
    setIsChatEmoticonSelectBoxOpen,
    selectedBlockEmoticon,
    clearSelectedBlockEmoticon,
    setIsChatNudgeSent
  } = useChatPanelStore(state_2 => ({
    closeChatInputArea: state_2.closeChatInputArea,
    setIsChatEmoticonSelectBoxOpen: state_2.setIsChatEmoticonSelectBoxOpen,
    selectedBlockEmoticon: state_2.selectedBlockEmoticon,
    clearSelectedBlockEmoticon: state_2.clearSelectedBlockEmoticon,
    setIsChatNudgeSent: state_2.setIsChatNudgeSent
  }));
  const bjIdx = playInfo?.media.userIdx || 0;
  const myIdx = userInfo?.idx;
  const {
    data: adChatData
  } = useAdChatConfigQuery({
    type: 'ad'
  });
  const {
    mutate: saveAdChat
  } = useSaveAdChatMutation();
  const appendChatErrorMessage = (message: string) => {
    setChatMessages(prev => {
      const now = Date.now();
      const newPayload: IntendedAlertMessage = {
        data: {
          type: 'IntendedAlert',
          message
        },
        offset: now
      };
      return [...prev, newPayload];
    });
  };
  const handleChatMessageError = (error: unknown) => {
    if (!(error instanceof BaseHttpResponseError)) {
      appendChatErrorMessage(messageOnUnexpectedChatError);
      return;
    }
    const {
      errorData
    } = error.serverResponse;
    if (!errorData?.code) return;
    if (errorData.code === 'FreezeOn') {
      onFreezeOnError?.(error.serverResponse.message);
      return;
    }
    const now_0 = Date.now();
    if (errorData.code === 'PaperingOn') {
      appendChatErrorMessage(messageOnPaperingOnError);
      setPaperingBlockTime(errorData.paperingBlockTime ?? 0);
      return;
    }
    if (setChatMessages && errorData && errorData.code === 'MuteOn') {
      setMuteTime(errorData.muteTime || 0);
      setChatMessages(prev_0 => {
        const newMutePayload: CustomMuteOnMessage = {
          data: {
            type: 'CustomMuteOn',
            message: {
              idx: errorData.idx || 0,
              muteTime: errorData.muteTime || 0
            },
            created_at: now_0
          },
          offset: now_0
        };
        return [...prev_0, newMutePayload];
      });
    }
  };
  const {
    mutate: postChatMessage
  } = useChatMessageMutation({
    onSuccess: () => {
      setIsChatNudgeSent(true);
    },
    onError: handleChatMessageError
  });
  const handleChatInputClick = () => {
    if (!SettingConfig.isFanza && !isLogin) {
      onNeedLogin?.();
      return;
    }
    if (!SettingConfig.isFanza && !isAuthDone) {
      onNeedAuthDone?.();
      return;
    }
    // 인풋창 클릭시 포커스가 갈때 키보드 패드가 생성되므로, 이모티콘은 닫기
    setIsChatEmoticonSelectBoxOpen(false);
    setTypingStart(dayjs().format('YYYY-MM-DD HH:mm:ss.SSS'));
  };
  const handleSubmitChat = async (intentionalMessage?: string) => {
    if (!playInfo || !token) return;
    const messageToSend = intentionalMessage || chatInputValue;

    // 로그인 및 인증 체크
    if (!SettingConfig.isFanza && !isLogin) {
      onNeedLogin?.();
      return;
    }
    if (!SettingConfig.isFanza && !isAuthDone) {
      onNeedAuthDone?.();
      return;
    }

    // 채팅 내용 초기화
    setChatInputValue('');

    // 내용 없이 채팅 전송 시도하면 채팅창 닫기
    if (messageToSend.trim() === '' && !selectedBlockEmoticon) {
      closeChatInputArea();
      return;
    }
    const now_1 = Date.now();

    // 도배로 인한 채팅 금지 상태인 경우
    if (now_1 < paperingBlockTime) {
      appendChatErrorMessage(messageOnSubmitChatOnPaperingBlockTime);
      return;
    }

    // 채팅금지 상태인 경우
    if (now_1 < muteTime) {
      appendChatErrorMessage(messageOnSubmitChatOnMuteTime);
      return;
    }

    // ---------freeze 상태에서 채팅 허용 여부 체크---------
    const fanInfo = playInfo.fan;
    // jwt payload 에서 최신 유저정보 파싱
    const tokenInfo = payloadInfo(token);
    if (tokenInfo) {
      fanInfo.level = tokenInfo.info?.lev ?? 0;
      fanInfo.rank = tokenInfo.info?.rk ?? 0;
      fanInfo.isManager = tokenInfo.info.type === 'manager';
    }
    if (playInfo.roomFreezeInfo?.enable) {
      const {
        roomFreezeInfo
      } = playInfo;
      const fanExcpt = roomFreezeInfo?.fanExcpt;
      if (fanInfo.isRoomMaster) {
        // 방장 채팅 허용
      } else if (fanExcpt !== 'N' && fanInfo.rank >= 1 && fanInfo.rank <= 10) {
        // 팬 예외 N이 아닌 상태(팬 예외)이면 열혈팬 rank 1~10 채팅 허용
      } else if (roomFreezeInfo?.fanExcpt && roomFreezeInfo.fanExcpt !== 'N' && Number(fanExcpt) <= Number(fanInfo.level)) {
        // 팬 예외 N이 아니고(빈 문자열도 아니고) 팬이면서 팬 예외 레벨보다 팬 레벨이 높으면 채팅 허용
        // 팬예외 레벨 1(브론즈)가 제한이고 시청자가 2(실버)라면 채팅 허용
      } else if (roomFreezeInfo?.managerExcpt === 'Y' && fanInfo.isManager) {
        // 내가 매니저이면서 채팅금지에서 매니저가 예외인 경우 채팅 허용
      } else {
        // 그 외 채팅 불가
        setChatMessages(prev_1 => {
          const newFreezePayload: FrozenMessage = {
            data: {
              type: 'Frozen',
              message: messageOnSubmitChatOnFrozen
            },
            offset: now_1
          };
          return [...prev_1, newFreezePayload];
        });
        return;
      }
    }

    // ---------광고성 채팅 체크---------
    const replacedMessage = toHalfLetter(messageToSend).replace(/[^a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ]/g, '').toUpperCase();
    const isAdChat = adChatData ? checkIsAdChat(adChatData, replacedMessage) : false;
    const isSuspiciousUser = userInfo?.purchaseUser === 'N' && !playInfo.fan.isRoomMaster && !playInfo.fan.isManager && bjIdx !== myIdx && adChatData?.adConfig.saveAdChat && playInfo.fan.level === 0;

    // 광고성 의심 유저 채팅 정보 서버에 저장
    if (isSuspiciousUser) {
      const currentDayjs = dayjs();
      const currentDayjsFull = currentDayjs.format('YYYY-MM-DD HH:mm:ss');
      const currentDayjsMs = currentDayjs.format('YYYY-MM-DD HH:mm:ss.SSS');
      const timeToFirstChat = dayjs(currentDayjsFull).diff(roomEnterTime.current, 'second');
      const messageLength = messageToSend.length;
      if (timeToFirstChat < adChatData.adConfig.chatFilterTime && messageLength >= adChatData.adConfig.chatFilterText) {
        const typingPerSec = messageLength * 1000 / dayjs(currentDayjsMs).diff(typingStart, 'millisecond');
        const saveAdChatParam: SaveAdChatParams = {
          replacedChat: replacedMessage,
          roomUserIdx: bjIdx,
          mediaCode: playInfo.media.code || '',
          roomEnterTime: roomEnterTime.current,
          chatTime: currentDayjsFull,
          chatFilterTime: adChatData.adConfig.chatFilterTime,
          chatFilterText: adChatData.adConfig.chatFilterText,
          keyControl: lastKeyPressedInChatInput,
          typingPerSec,
          adChat: isAdChat ? 'Y' : 'N',
          chat: messageToSend
        };
        saveAdChat(saveAdChatParam);
      }
    }

    // 광고성 채팅일 경우 가짜 채팅 메시지 추가
    if (isAdChat) {
      let type: NormalChatPayloadType = 'chatter';
      if (playInfo?.fan.isManager) {
        type = 'manager';
      }
      if (playInfo?.fan.isRoomMaster) {
        type = 'bj';
      }
      const fakeChatMessage: NormalChatMessage = {
        data: {
          message: messageToSend,
          emoticon: selectedBlockEmoticon ? {
            block: selectedBlockEmoticon,
            inline: null
          } : null,
          type,
          lev: playInfo?.fan.level as FanLevel,
          rk: playInfo?.fan.rank || 0,
          created_at: Date.now(),
          id: userInfo?.id || '',
          sex: userInfo?.gender === 'M' ? 'M' : 'F',
          idx: userInfo?.idx || 0,
          nk: userInfo?.nick || '',
          dt: checkIsMobile() ? 'webMobile' : 'webPc',
          pf: 'wpm',
          iat: Date.now(),
          ip: '',
          lang: ['ko']
        },
        offset: Date.now()
      };
      setChatMessages(prev_2 => [...prev_2, fakeChatMessage]);
      return;
    }

    // ---------채팅 전송---------
    postChatMessage({
      message: messageToSend,
      ...(selectedBlockEmoticon && {
        emoticonIdx: selectedBlockEmoticon.idx
      }),
      token,
      roomid: playInfo.media.code || '',
      channel: playInfo.media.userIdx,
      t: playInfo.chatServer.t,
      chatToken: playInfo.chatServer.token
    });
    clearSelectedBlockEmoticon();
  };
  const handleChatInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!isLogin) return;
    if ((event as any)?.isComposing || event?.keyCode === 229) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSubmitChat();
      return;
    }
    if (event.ctrlKey && event.key === 'v') {
      setLastKeyPressedInChatInput('cv');
    } else {
      setLastKeyPressedInChatInput('any');
    }
  };
  useInduceBookmark();
  useChatInitiator();
  const enterChat = playInfo ? parseEnterChat(playInfo) : null;
  return {
    chatInputRef,
    handleChatInputKeyDown,
    handleChatInputClick,
    handleSubmitChat,
    enterChat
  };
};
export default useSendChatLogicAndCommonHooks;